import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  FileText,
  MagnifyingGlass,
  CircleNotch,
  Copy,
  CheckCircle,
  CaretLeft,
  Globe,
  Tag,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  APPROVED: "bg-[#D1FAE5] text-[#065F46]",
  PENDING: "bg-[#FEF3C7] text-[#92400E]",
  REJECTED: "bg-[#FEE2E2] text-[#991B1B]",
  PAUSED: "bg-[#E5E7EB] text-[#374151]",
};

const CATEGORY_STYLES = {
  AUTHENTICATION: "bg-[#E0E7FF] text-[#3730A3]",
  MARKETING: "bg-[#FCE7F3] text-[#9D174D]",
  UTILITY: "bg-[#DBEAFE] text-[#1E40AF]",
};

function chip(cls, label) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cls}`}>
      {label}
    </span>
  );
}

function PreviewBubble({ components }) {
  const header = components.find((c) => c.type === "HEADER");
  const body = components.find((c) => c.type === "BODY");
  const footer = components.find((c) => c.type === "FOOTER");
  const buttons = components.find((c) => c.type === "BUTTONS");
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[#DCF8C6] dark:bg-[#005c4b] p-3 max-w-md space-y-1.5">
      {header?.text && (
        <div className="text-sm font-semibold text-[#064E3B] dark:text-white/95">
          {header.text}
        </div>
      )}
      {body?.text && (
        <div className="text-sm text-[#111827] dark:text-white/95 whitespace-pre-wrap">
          {body.text}
        </div>
      )}
      {footer?.text && (
        <div className="text-[11px] text-[#4B5563] dark:text-white/70 pt-1">
          {footer.text}
        </div>
      )}
      {buttons?.buttons?.length > 0 && (
        <div className="pt-2 border-t border-white/30 space-y-1">
          {buttons.buttons.map((b, i) => (
            <div
              key={i}
              className="text-xs text-[#1D4ED8] dark:text-[#93C5FD] text-center py-1 bg-white dark:bg-black/20 rounded-md"
            >
              {b.type === "URL" ? "🔗 " : b.type === "PHONE_NUMBER" ? "📞 " : ""}
              {b.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TemplatesDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(null); // which block was copied: 'def' | 'send'
  const [to, setTo] = useState("");
  const [vars, setVars] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(null);
    api
      .get("/whatsapp/templates")
      .then((r) => setData(r.data))
      .catch(() => setData({ templates: [], configured: false, error: "Failed to fetch" }))
      .finally(() => setLoading(false));
  }, [open]);

  // When a template is selected, reset inputs
  useEffect(() => {
    setTo("");
    setVars({});
  }, [selected?.name]);

  const items = useMemo(() => {
    const arr = data?.templates || [];
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.category || "").toLowerCase().includes(q) ||
        (t.language || "").toLowerCase().includes(q),
    );
  }, [data, search]);

  const copyJson = async (obj, kind = "def") => {
    const text = typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(kind);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      toast.error("Copy failed — select and copy manually");
    }
  };

  // Extract distinct {{N}} placeholders across all components in order,
  // and grab Meta-saved example values so we can show them as input hints.
  const placeholders = useMemo(() => {
    if (!selected) return { header: [], body: [], buttons: [], examples: {} };
    const grab = (txt = "") => {
      const matches = [...String(txt).matchAll(/\{\{(\d+)\}\}/g)];
      return [...new Set(matches.map((m) => Number(m[1])))].sort((a, b) => a - b);
    };
    const header = selected.components?.find((c) => c.type === "HEADER");
    const body = selected.components?.find((c) => c.type === "BODY");
    const buttons = selected.components?.find((c) => c.type === "BUTTONS");

    // Meta stores example values inside each component
    //   BODY:   example.body_text = [["val1","val2",...]]
    //   HEADER: example.header_text = ["val1"]
    //   BUTTON: example = ["url_suffix1",...]
    const examples = { header: {}, body: {}, buttons: {} };
    const bodyNums = grab(body?.text || "");
    const bodyEx = body?.example?.body_text?.[0] || [];
    bodyNums.forEach((n, i) => {
      if (bodyEx[i] !== undefined) examples.body[n] = String(bodyEx[i]);
    });
    const headerNums = header?.format === "TEXT" ? grab(header.text) : [];
    const headerEx = header?.example?.header_text || [];
    headerNums.forEach((n, i) => {
      if (headerEx[i] !== undefined) examples.header[n] = String(headerEx[i]);
    });
    const btnList = [];
    (buttons?.buttons || []).forEach((b, bi) => {
      if (b.type !== "URL" || !b.url) return;
      const nums = grab(b.url);
      const btnEx = Array.isArray(b.example) ? b.example : [];
      nums.forEach((n, i) => {
        btnList.push({ n, btnIndex: bi });
        // Meta example for URL button is typically full resolved URL. Extract by diffing.
        const raw = btnEx[i];
        if (raw !== undefined) {
          // Best-effort: strip the literal parts of the original URL template.
          const parts = b.url.split(/\{\{\d+\}\}/);
          let val = String(raw);
          parts.forEach((p) => { if (p) val = val.replace(p, ""); });
          examples.buttons[`${bi}_${n}`] = val;
        }
      });
    });

    return { header: headerNums, body: bodyNums, buttons: btnList, examples };
  }, [selected]);

  // Build the Meta /messages API payload using current var values
  const sendPayload = useMemo(() => {
    if (!selected) return null;
    const components = [];
    const v = (n, fallback) => {
      const val = (vars[`body_${n}`] ?? "").trim();
      return val || fallback;
    };

    if (placeholders.header.length > 0) {
      components.push({
        type: "header",
        parameters: placeholders.header.map((n) => ({
          type: "text",
          text: (vars[`header_${n}`] ?? "").trim() || `{{${n}}}`,
        })),
      });
    }
    if (placeholders.body.length > 0) {
      components.push({
        type: "body",
        parameters: placeholders.body.map((n) => ({
          type: "text",
          text: v(n, `{{${n}}}`),
        })),
      });
    }
    // URL button parameters (sub_type=url, index per button)
    const urlBtns = placeholders.buttons;
    if (urlBtns.length > 0) {
      const grouped = {};
      urlBtns.forEach(({ n, btnIndex }) => {
        (grouped[btnIndex] ||= []).push(n);
      });
      Object.entries(grouped).forEach(([btnIndex, nums]) => {
        components.push({
          type: "button",
          sub_type: "url",
          index: String(btnIndex),
          parameters: nums.map((n) => ({
            type: "text",
            text: (vars[`btn_${btnIndex}_${n}`] ?? "").trim() || `{{${n}}}`,
          })),
        });
      });
    }

    return {
      messaging_product: "whatsapp",
      to: (to || "").replace(/[^\d]/g, "") || "<RECIPIENT_PHONE>",
      type: "template",
      template: {
        name: selected.name,
        language: { code: selected.language || "en" },
        ...(components.length > 0 ? { components } : {}),
      },
    };
  }, [selected, vars, to, placeholders]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="templates-dialog"
      >
        <DialogHeader className="p-5 pb-3 border-b border-[var(--border)] shrink-0">
          <DialogTitle className="flex items-center gap-2 heading">
            <FileText size={20} weight="fill" className="text-[#3B82F6]" />
            {selected ? (
              <button
                onClick={() => setSelected(null)}
                className="flex items-center gap-1.5 text-[var(--fg)] hover:opacity-80"
                data-testid="templates-back-btn"
              >
                <CaretLeft size={16} weight="bold" />
                {selected.name}
              </button>
            ) : (
              <>WhatsApp Templates</>
            )}
          </DialogTitle>
          <DialogDescription>
            {selected
              ? "Preview, components and full JSON payload for this template."
              : `All templates from your Meta Business account (${data?.count ?? 0} total). Click any template to view its full payload.`}
          </DialogDescription>
        </DialogHeader>

        {/* List view */}
        {!selected && (
          <>
            <div className="px-5 pt-3 pb-2 shrink-0">
              <div className="relative">
                <MagnifyingGlass
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, category, language…"
                  className="pl-9 h-9"
                  data-testid="templates-search-input"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-5">
              {loading ? (
                <div className="py-10 flex items-center justify-center text-[var(--muted)] text-sm gap-2">
                  <CircleNotch size={18} className="animate-spin" /> Loading templates…
                </div>
              ) : !data?.configured ? (
                <div className="py-10 text-center text-sm text-[var(--muted)]">
                  {data?.hint || "WhatsApp templates not configured."}
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center text-sm text-[var(--muted)]">
                  No templates match your search.
                </div>
              ) : (
                <ul className="space-y-2">
                  {items.map((t) => {
                    const body = t.components?.find((c) => c.type === "BODY");
                    const preview = (body?.text || "").slice(0, 140);
                    return (
                      <li key={`${t.name}-${t.language}`}>
                        <button
                          type="button"
                          onClick={() => setSelected(t)}
                          data-testid={`template-item-${t.name}`}
                          className="w-full text-left rounded-lg border border-[var(--border)] p-3 hover:bg-[var(--bg)] transition-colors"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-sm font-mono">{t.name}</div>
                            {chip(STATUS_STYLES[t.status] || "bg-[var(--bg)] text-[var(--muted)]", t.status)}
                            {chip(CATEGORY_STYLES[t.category] || "bg-[var(--bg)] text-[var(--muted)]", t.category)}
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg)] text-[var(--muted)]">
                              <Globe size={10} weight="bold" />
                              {t.language}
                            </span>
                          </div>
                          {preview && (
                            <div className="text-xs text-[var(--muted)] mt-1.5 line-clamp-2">
                              {preview}
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}

        {/* Detail view */}
        {selected && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {chip(STATUS_STYLES[selected.status] || "bg-[var(--bg)] text-[var(--muted)]", selected.status)}
              {chip(CATEGORY_STYLES[selected.category] || "bg-[var(--bg)] text-[var(--muted)]", selected.category)}
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg)] text-[var(--muted)]">
                <Globe size={10} weight="bold" />
                {selected.language}
              </span>
              {selected.id && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg)] text-[var(--muted)] font-mono">
                  <Tag size={10} weight="bold" />
                  {selected.id}
                </span>
              )}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-semibold mb-2">
                Preview
              </div>
              <PreviewBubble components={selected.components || []} />
            </div>

            {/* Send-ready Meta /messages payload */}
            <div className="border-t border-[var(--border)] pt-4">
              <div className="mb-2">
                <div className="text-[11px] uppercase tracking-wide text-[#10B981] font-semibold flex items-center gap-1.5">
                  📤 Send API Payload
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-0.5">
                  Ye payload directly <code className="font-mono">POST https://graph.facebook.com/{`{version}`}/{`{phone_number_id}`}/messages</code> par bhejo. Variables fill karo ya placeholder chhod do.
                </div>
              </div>

              {/* Recipient + variable inputs */}
              <div className="space-y-2 mb-3">
                <div>
                  <label className="text-[11px] font-semibold text-[var(--muted)]">Recipient phone (E.164, no +)</label>
                  <Input
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="e.g. 917488296076"
                    className="h-8 text-sm font-mono mt-1"
                    data-testid="template-to-input"
                  />
                </div>

                {placeholders.header.length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold text-[var(--muted)] mb-1">HEADER variables</div>
                    {placeholders.header.map((n) => {
                      const sample = placeholders.examples?.header?.[n];
                      return (
                        <div key={`h${n}`} className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] font-mono w-10 text-[var(--muted)]">{`{{${n}}}`}</span>
                          <Input
                            value={vars[`header_${n}`] || ""}
                            onChange={(e) => setVars({ ...vars, [`header_${n}`]: e.target.value })}
                            placeholder={sample ? `e.g. ${sample}` : `Value for header {{${n}}}`}
                            className="h-8 text-sm flex-1"
                            data-testid={`template-var-header-${n}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {placeholders.body.length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold text-[var(--muted)] mb-1">BODY variables</div>
                    {placeholders.body.map((n) => {
                      const sample = placeholders.examples?.body?.[n];
                      return (
                        <div key={`b${n}`} className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] font-mono w-10 text-[var(--muted)]">{`{{${n}}}`}</span>
                          <Input
                            value={vars[`body_${n}`] || ""}
                            onChange={(e) => setVars({ ...vars, [`body_${n}`]: e.target.value })}
                            placeholder={sample ? `e.g. ${sample}` : `Value for body {{${n}}}`}
                            className="h-8 text-sm flex-1"
                            data-testid={`template-var-body-${n}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {placeholders.buttons.length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold text-[var(--muted)] mb-1">URL BUTTON variables</div>
                    {placeholders.buttons.map(({ n, btnIndex }) => {
                      const sample = placeholders.examples?.buttons?.[`${btnIndex}_${n}`];
                      return (
                        <div key={`btn${btnIndex}-${n}`} className="flex items-center gap-2 mt-1">
                          <span className="text-[11px] font-mono w-20 text-[var(--muted)]">btn[{btnIndex}] {`{{${n}}}`}</span>
                          <Input
                            value={vars[`btn_${btnIndex}_${n}`] || ""}
                            onChange={(e) => setVars({ ...vars, [`btn_${btnIndex}_${n}`]: e.target.value })}
                            placeholder={sample ? `e.g. ${sample}` : `Value for button[${btnIndex}] {{${n}}}`}
                            className="h-8 text-sm flex-1"
                            data-testid={`template-var-btn-${btnIndex}-${n}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-semibold">
                  Generated POST body
                </div>
                <Button
                  variant="default"
                  size="sm"
                  className="h-7 text-xs gap-1.5 bg-[#10B981] hover:bg-[#059669] text-white"
                  onClick={() => copyJson(sendPayload, "send")}
                  data-testid="template-copy-send-btn"
                >
                  {copied === "send" ? (
                    <>
                      <CheckCircle size={12} weight="fill" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy size={12} weight="fill" /> Copy Send Payload
                    </>
                  )}
                </Button>
              </div>
              <pre
                className="text-[11px] font-mono whitespace-pre-wrap break-all bg-[#064E3B]/10 border border-[#10B981]/40 rounded-md p-3 max-h-80 overflow-y-auto"
                data-testid="template-send-payload-json"
              >
                {JSON.stringify(sendPayload, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
